import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import { AppInfo } from 'utils/AppInfo';
import { Environment } from 'utils/EnvironmentHelper';

const history = createBrowserHistory();

/**
 * Collects context to identify browser extensions or injected scripts.
 */
function collectThirdPartyDiagnostics(): Record<string, unknown> {
    const diagnostics: Record<string, unknown> = {};

    try {
        const scripts = Array.from(document.scripts)
            .map((s) => s.src)
            .filter((src) => src && !src.includes(window.location.host))
            .slice(0, 20);
        diagnostics.externalScripts = scripts;
    } catch (e) {
        diagnostics.externalScripts = `Error collecting: ${
            e instanceof Error ? e.message : String(e)
        }`;
    }

    try {
        const detectedGlobals: string[] = [];
        const suspectGlobals = [
            '__BITWARDEN_POPUP__',
            'lastpass',
            'LastPass',
            '__1PASSWORD__',
            'Symantec',
            'McAfee',
            'Zscaler',
            'CrowdStrike',
            'CarbonBlack',
            'protobuf',
            '__proto_buf__',
            'google_protobuf',
            'NordVPN',
            'ExpressVPN',
            'Intercom',
            'Zendesk',
            'Drift',
        ];
        for (const global of suspectGlobals) {
            if (global in window) {
                detectedGlobals.push(global);
            }
        }
        diagnostics.detectedGlobals = detectedGlobals;
    } catch (e) {
        diagnostics.detectedGlobals = `Error collecting: ${
            e instanceof Error ? e.message : String(e)
        }`;
    }

    try {
        const inlineScripts = Array.from(document.scripts).filter((s) => !s.src).length;
        diagnostics.inlineScriptCount = inlineScripts;
    } catch (e) {
        diagnostics.inlineScriptCount = `Error collecting: ${
            e instanceof Error ? e.message : String(e)
        }`;
    }

    try {
        const uaData = (navigator as Navigator & {
            userAgentData?: { brands?: { brand: string }[] };
        }).userAgentData;
        if (uaData?.brands) {
            diagnostics.browserBrands = uaData.brands.map((b) => b.brand);
        }
    } catch (e) {
        diagnostics.browserBrands = `Error collecting: ${
            e instanceof Error ? e.message : String(e)
        }`;
    }

    return diagnostics;
}

/**
 * Checks if an error is a network-related fetch failure.
 * These occur due to: network disconnection, request timeout, user navigation,
 * server unreachable, CORS issues, or aborted requests.
 */
function isNetworkError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }

    const message = error.message.toLowerCase();
    return (
        message.includes('failed to fetch') ||
        message.includes('load failed') ||
        message.includes('networkerror') ||
        message.includes('network request failed') ||
        message.includes('aborted')
    );
}

/**
 * Checks if an error is an empty object rejection WITHOUT a stacktrace.
 * These typically come from browser extensions, AbortController errors,
 * or third-party scripts that reject with empty objects.
 * Without a stacktrace, these are not actionable.
 *
 * Both conditions must be true:
 * 1. The error looks like an empty rejection (either an empty object literal,
 *    or Sentry's message matches known empty-rejection patterns)
 * 2. There is no useful stacktrace to debug with
 */
function isEmptyObjectRejection(error: unknown, event: Sentry.ErrorEvent): boolean {
    const isEmptyObject =
        error !== null &&
        typeof error === 'object' &&
        !(error instanceof Error) &&
        Object.keys(error).length === 0;

    const message = event.exception?.values?.[0]?.value ?? '';
    const hasEmptyRejectionMessage =
        message.includes('Object captured as promise rejection with keys') ||
        message.includes('[object has no keys]');

    const looksLikeEmptyRejection = isEmptyObject || hasEmptyRejectionMessage;

    if (!looksLikeEmptyRejection) {
        return false;
    }

    const hasUsefulStacktrace = event.exception?.values?.some((exception) =>
        exception.stacktrace?.frames?.some(
            (frame) =>
                // Frame must have a filename and not be from node_modules or browser internals
                frame.filename &&
                !frame.filename.includes('node_modules') &&
                !frame.filename.startsWith('chrome-extension://') &&
                !frame.filename.startsWith('moz-extension://') &&
                !frame.filename.includes('<anonymous>')
        )
    );

    return !hasUsefulStacktrace;
}

/**
 * Checks if an error is a DOM manipulation error caused by external interference.
 * These occur when browser extensions (Grammarly, LastPass, Google Translate, etc.)
 * modify the DOM outside of React's control, causing React's reconciliation to fail.
 */
function isExternalDomManipulationError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }

    const message = error.message.toLowerCase();
    return (
        message.includes("failed to execute 'removechild'") ||
        message.includes("failed to execute 'insertbefore'") ||
        message.includes("failed to execute 'appendchild'") ||
        message.includes('is not a child of this node') ||
        message.includes('node was not found') ||
        message.includes("failed to execute 'removeattribute'")
    );
}

/**
 * Checks if an error is a utf8Write/protobuf error from third-party scripts.
 * These come from browser extensions that inject protobuf encoding and fail
 * during serialization — not actionable from our side.
 */
function isUtf8WriteError(error: unknown, event: Sentry.ErrorEvent): boolean {
    return (
        error instanceof Error &&
        error.message.includes('utf8Write') &&
        (event.exception?.values?.some((v) =>
            v.stacktrace?.frames?.some(
                (f) =>
                    f.function?.includes('encode_Encoder') ||
                    f.function?.includes('Encoder')
            )
        ) ??
            false)
    );
}

/**
 * Sentry beforeSend hook that categorizes and enriches errors:
 * - Downgrades network errors and empty object rejections to warning level
 * - Tags and enriches DOM manipulation errors with third-party diagnostics
 * - Tags and enriches utf8Write/protobuf errors with third-party diagnostics
 */
function beforeSend(
    event: Sentry.ErrorEvent,
    hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
    try {
        const error = hint.originalException;

        // Downgrade network errors to warning level - these are environmental,
        // not bugs. Still track them but with lower severity for monitoring patterns.
        if (isNetworkError(error)) {
            event.level = 'warning';
            event.tags = {
                ...event.tags,
                errorCategory: 'network',
            };
            event.contexts = {
                ...event.contexts,
                networkError: {
                    url: window.location.href,
                    online: navigator.onLine,
                    timestamp: new Date().toISOString(),
                },
            };
        }

        // Downgrade empty object rejections - these come from browser extensions,
        // third-party scripts, or AbortController errors. Without stacktraces,
        // they're not actionable.
        else if (isEmptyObjectRejection(error, event)) {
            event.level = 'warning';
            event.tags = {
                ...event.tags,
                errorCategory: 'empty-rejection',
            };
            event.contexts = {
                ...event.contexts,
                emptyRejection: {
                    url: window.location.href,
                    timestamp: new Date().toISOString(),
                },
                thirdPartyDiagnostics: collectThirdPartyDiagnostics(),
            };
        }

        // Track DOM manipulation errors with diagnostics - these are typically caused
        // by browser extensions (Grammarly, LastPass, Google Translate) modifying the DOM.
        // We don't downgrade these yet since we want to gather data first.
        else if (isExternalDomManipulationError(error)) {
            event.tags = {
                ...event.tags,
                errorCategory: 'dom-manipulation',
            };
            event.contexts = {
                ...event.contexts,
                domManipulationError: {
                    url: window.location.href,
                    timestamp: new Date().toISOString(),
                },
                thirdPartyDiagnostics: collectThirdPartyDiagnostics(),
            };
        } else if (isUtf8WriteError(error, event)) {
            event.contexts = {
                ...event.contexts,
                thirdPartyDiagnostics: collectThirdPartyDiagnostics(),
            };
            event.tags = {
                ...event.tags,
                errorCategory: 'utf8Write-protobuf',
            };
        }
    } catch (e) {
        // Never lose the original event due to classification errors.
        // Log via Sentry's separate logging pipeline so we know if beforeSend itself is failing.
        Sentry.logger.warn('beforeSend classification error', {
            error: e instanceof Error ? e.message : String(e),
        });
    }

    return event;
}

const debugMode = new URL(window.location.href).searchParams.has('sentryDebugLogging');
function log(...params: unknown[]) {
    if (debugMode) {
        // eslint-disable-next-line no-console
        console.log(...params);
    }
}
log('dsn', Environment.sentryUrl);
log('env', Environment.kcfEnv + (Environment.isLocalDevelopment ? '-local' : ''));
log('isSentryEnabled', Environment.isSentryEnabled);

Sentry.init({
    dsn: Environment.sentryUrl,
    environment: Environment.kcfEnv + (Environment.isLocalDevelopment ? '-local' : ''),
    release: AppInfo.displayVersion(),
    beforeSend,
    integrations: [
        Sentry.breadcrumbsIntegration(),
        Sentry.browserApiErrorsIntegration(),
        Sentry.browserSessionIntegration(),
        Sentry.dedupeIntegration(),
        Sentry.functionToStringIntegration(),
        Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true,
        }),
        Sentry.httpContextIntegration(),
        Sentry.linkedErrorsIntegration(),

        // See docs for support of different versions of variation of react router
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
        Sentry.reactRouterV5BrowserTracingIntegration({ history }),
    ],
    enabled: Environment.isSentryEnabled,

    sampleRate: 1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    debug: debugMode,
});

if (debugMode) {
    Sentry.logger.info('sentry initialized with debug logging');
}
